import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Award, Landmark, ArrowLeft, Link as LinkIcon, Linkedin, Check } from "lucide-react";
import { toast } from "sonner";

export default function Certificate() {
  const { moduleId } = useParams();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/certificate/${moduleId}`)
      .then((r) => setCert(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [moduleId]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">Not yet</div>
        <h1 className="font-serif text-3xl font-bold text-[#0A0A0A] mb-4">Certificate locked</h1>
        <p className="text-[#525252] mb-6">{error}</p>
        <Link to={`/modules/${moduleId}`} className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#0A0A0A] text-[#FFFFFF]">
          <ArrowLeft className="w-4 h-4" /> Back to module
        </Link>
      </div>
    );
  }

  if (!cert) return <div className="max-w-4xl mx-auto p-12 text-[#525252]">Loading…</div>;

  const issued = new Date(cert.issued_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="max-w-4xl mx-auto px-6 py-14">
      <Link to={`/modules/${moduleId}`} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#525252] hover:text-[#0A0A0A] mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to module
      </Link>

      <div data-testid="certificate-card" className="border-2 border-[#0A0A0A] p-12 bg-[#FFFFFF] relative">
        <div className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252]">
          {cert.cert_id}
        </div>

        <div className="flex items-center gap-3 mb-12">
          <Landmark className="w-6 h-6 text-[#0A0A0A]" strokeWidth={1.75} />
          <div>
            <div className="font-serif text-lg font-bold text-[#0A0A0A]">YesVersity</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252]">Certificate of completion</div>
          </div>
        </div>

        <div className="mb-10">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">This certifies that</div>
          <div data-testid="certificate-name" className="font-serif text-5xl font-black text-[#0A0A0A] mb-8">{cert.user_name}</div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">has completed</div>
          <div className="font-serif text-3xl font-bold text-[#0A0A0A] mb-2">
            <span className="text-[#C5A059] font-mono text-xl mr-3">{cert.module_number}</span>
            {cert.module_title}
          </div>
        </div>

        <hr className="border-[#E5E5E0] my-8" />

        <div className="flex items-end justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] mb-1">Issued</div>
            <div className="font-serif text-lg font-semibold text-[#0A0A0A]">{issued}</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] mb-1">Signed</div>
            <div className="font-serif italic text-lg text-[#0A0A0A]">The Faculty</div>
          </div>
          <Award className="w-16 h-16 text-[#C5A059]" strokeWidth={1.25} />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button
          data-testid="certificate-copy-link"
          onClick={() => {
            const url = `${window.location.origin}/c/${cert.cert_id}`;
            navigator.clipboard.writeText(url).then(() => toast.success("Public link copied"));
          }}
          className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFFFFF] transition-colors text-sm inline-flex items-center gap-2"
        >
          <LinkIcon className="w-4 h-4" /> Copy public link
        </button>
        <a
          data-testid="certificate-linkedin"
          href={(() => {
            const issuedDate = new Date(cert.issued_at);
            const params = new URLSearchParams({
              startTask: "CERTIFICATION_NAME",
              name: `${cert.module_number} · ${cert.module_title}`,
              organizationName: "YesVersity",
              issueYear: String(issuedDate.getFullYear()),
              issueMonth: String(issuedDate.getMonth() + 1),
              certUrl: `${window.location.origin}/c/${cert.cert_id}`,
              certId: cert.cert_id,
            });
            return `https://www.linkedin.com/profile/add?${params.toString()}`;
          })()}
          target="_blank"
          rel="noreferrer noopener"
          className="px-5 py-2.5 bg-[#0A66C2] text-white hover:bg-[#004182] transition-colors text-sm inline-flex items-center gap-2"
        >
          <Linkedin className="w-4 h-4" /> Add to LinkedIn
        </a>
        <button
          data-testid="certificate-print"
          onClick={() => window.print()}
          className="px-5 py-2.5 bg-[#0A0A0A] text-[#FFFFFF] hover:bg-[#000000] transition-colors text-sm"
        >
          Print / Save as PDF
        </button>
      </div>

      <div className="mt-4 p-4 border border-[#E5E5E0] bg-[#F5F5F0] text-xs font-mono text-[#525252] break-all flex items-center gap-2">
        <Check className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
        <span data-testid="certificate-public-url">{`${typeof window !== "undefined" ? window.location.origin : ""}/c/${cert.cert_id}`}</span>
      </div>
    </div>
  );
}
